import { Suspense } from "react"
import { faShieldCheck } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Card, CardContent, Grid, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"

import { SUPPLIER_ADMIN_ROUTE } from "../../../../../../constants/routes"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { FetchAcquirerAgreementsResponse, fetchAcquirerAgreementsForSupplier, POSQuery } from "../SupplierAdminPOSApi"
import { SupplierAcquiringNoAgreement } from "../components/SupplierAcquiringNoAgreement"
import { SupplierAcquiringAgreementStatus } from "../components/SupplierAcquiringAgreementStatus"
import { AgreementSkeleton } from "../components/AgreementSkeleton"
import { WellknownAgreementType } from "../SupplierAdminPOSTypes"
import { AcquirerBankAccounts } from "../components/AcquirerBankAccounts"
import { AcquirerIndustryCodes } from "../components/AcquirerIndustryCodes"
import { useCurrentSupplier } from "../../../../../suppliers/supplierHooks"
import { isReadyForAgreementStatus, isReadyForBankAccounts, isReadyForIndustryCodes } from "../acquiringLib"
import { ExternalLink } from "../../../../../../shared/components/links/ExternalLink"

const ADYEN_WEBSITE_URL = "https://www.adyen.com"

const useStyles = makeStyles()((theme) => ({
    container: {
        gap: "1.5rem",
    },
    infoCard: {
        background: "#E5F6FD",
        borderRadius: "0.25rem",
        border: "none",
    },
    cardContentAnswer: {
        display: "grid",
        gap: "1rem",
        padding: "1.5rem 2.5rem",
        color: "#014361",
    },
    card: {
        borderColor: `${theme.palette.tailwind.slate[200]}`,
        borderRadius: theme.spacing(1.5),
        boxShadow: "0px 2px 6px 0px #00000014",
        padding: "2.5rem",
        width: "100%",
    },
    cardContent: {
        display: "flex",
        flexDirection: "column",
        gap: "2.5rem",
        padding: 0,
        "&:last-child": {
            padding: 0,
        },
    },
    cardHeader: {
        fontWeight: "500",
        color: "#014361",
    },
    cardContentLink: {
        transition: "all ease-in-out 0.3s",
    },
}))

function selectAcquirerAgreement(data: FetchAcquirerAgreementsResponse) {
    const agreement = data.agreements.find((agreement) => agreement.type === WellknownAgreementType.ADYEN_POS)
    return agreement
}

function AcquiringAgreement() {
    const { supplierId } = useCurrentSupplier()

    const { data: acquiringAgreement } = useApiQuery(fetchAcquirerAgreementsForSupplier, {
        queryName: POSQuery.AGREEMENTS_DETAILS,
        dependencies: {
            supplierId,
        },
        select: selectAcquirerAgreement,
        suspense: true,
        useErrorBoundary: true,
    })

    const { classes } = useStyles()

    return (
        <Grid container direction="column" wrap="nowrap" className={classes.container}>
            <Card variant="outlined" className={classes.infoCard}>
                <CardContent className={classes.cardContentAnswer}>
                    <LocalizedStrict id="supplier-admin-pos-acquirer-adyen-question">
                        <Typography variant="body1" className={classes.cardHeader}>
                            Who is Adyen?
                        </Typography>
                    </LocalizedStrict>
                    <LocalizedStrict id="supplier-admin-pos-acquirer-adyen-answer">
                        <Typography variant="body2">
                            Adyen is a our credit card payment company with the status of an acquiring bank that allows
                            you to accept credit card payments. Adyen is responsible for processing payments for your
                            company. Before you can start accepted payment cards you must apply for an agreement at
                            Adyen.
                        </Typography>
                    </LocalizedStrict>
                    <LocalizedStrict
                        id="supplier-admin-pos-acquirer-adyen-read-more"
                        vars={{ adyenWebsite: ADYEN_WEBSITE_URL }}
                        elems={{
                            externalLink: (
                                <ExternalLink
                                    href={ADYEN_WEBSITE_URL}
                                    className={classes.cardContentLink}
                                ></ExternalLink>
                            ),
                        }}
                    >
                        <Typography variant="body2">
                            Read more:{" "}
                            <ExternalLink href={ADYEN_WEBSITE_URL} className={classes.cardContentLink}>
                                {ADYEN_WEBSITE_URL}
                            </ExternalLink>
                        </Typography>
                    </LocalizedStrict>
                </CardContent>
            </Card>
            <Card variant="outlined" className={classes.card}>
                <CardContent className={classes.cardContent}>
                    {acquiringAgreement?.adyenAgreement &&
                    isReadyForAgreementStatus(acquiringAgreement.adyenAgreement.status) ? (
                        <SupplierAcquiringAgreementStatus
                            agreementId={acquiringAgreement.id}
                            status={acquiringAgreement.adyenAgreement.status}
                        />
                    ) : (
                        <SupplierAcquiringNoAgreement
                            accepted={acquiringAgreement?.termsAndConditions?.accepted ?? false}
                        />
                    )}
                </CardContent>
            </Card>
            {acquiringAgreement && isReadyForIndustryCodes(acquiringAgreement?.adyenAgreement?.status) && (
                <AcquirerIndustryCodes agreementId={acquiringAgreement.id} />
            )}
            {acquiringAgreement && isReadyForBankAccounts(acquiringAgreement.adyenAgreement?.status) && (
                <AcquirerBankAccounts agreementId={acquiringAgreement.id} />
            )}
        </Grid>
    )
}

type Props = Readonly<{}>

export function SupplierAcquiringAgreement({}: Props) {
    const { classes } = useStyles()

    return (
        <>
            <LocalizedStrict id="supplier-admin-pos-acquirer-page-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SUPPLIER_POS}
                    title="Payment acquiring agreement"
                    icon={<FontAwesomeIcon icon={faShieldCheck} />}
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="supplier-nav-item-home">
                            <BreadcrumbLink to={SUPPLIER_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-admin-nav-item-settings">
                            <Breadcrumb>Settings</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-admin-nav-item-acquiring">
                            <Breadcrumb>Acquiring</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <QueryErrorBoundary>
                <Suspense
                    fallback={
                        <Grid container className={classes.container}>
                            <Grid item xs={12}>
                                <Card variant="outlined" className={classes.card}>
                                    <CardContent className={classes.cardContent}>
                                        <AgreementSkeleton />
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    }
                >
                    <AcquiringAgreement />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}
